import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { fetchSubscriptionStatus } from '../../pages/player/api';
import { userActions } from '../../store/user/userSlice';
import { useLexicon } from '../../hooks/useLexicon';

// Render a lexicon template that contains {placeholders}, substituting each
// placeholder (in order) with a React node so bold/dynamic bits keep correct
// per-language word order. e.g. interp("A {x} B", [<b>1</b>]) -> A <b>1</b> B
const interp = (tmpl, nodes) => {
  const parts = String(tmpl).split(/\{[^}]+\}/);
  return parts.flatMap((p, i) => (i < nodes.length ? [p, nodes[i]] : [p]));
};

// 12h-precision trial banner — replaces the day-based banner. PRD §11.5.
//
// State table:
//   trial, >1h remaining   → "Trial active — Xh Ym remaining"
//   trial, ≤1h remaining   → "Trial ending in Ym. We'll charge €69 at HH:MM"
//   active                 → no banner
//   cancelled (in period)  → "Subscription ends DATE — you won't be charged again"
//   expired                → no banner (the 402 interceptor handles routing)
//
// Polls /subscription/status every 60s while visible so the countdown
// stays live; we re-render every second locally to update the Hh Mm copy.

const POLL_MS = 60_000;
const TICK_MS = 1_000;

const formatDateLong = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'long' });
};

const formatHourMinute = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
};

export const TrialBanner = ({ showUpgradeLink = true }) => {
  const dispatch = useDispatch();
  const l = useLexicon();
  const subscription = useSelector((state) => state.user.subscription);
  const [, forceRender] = useState(0);
  const tickRef = useRef(null);

  // Local 1s tick keeps the Xh Ym copy fresh between API polls.
  useEffect(() => {
    if (!subscription || subscription.status !== 'trial') return undefined;
    tickRef.current = setInterval(() => forceRender((n) => n + 1), TICK_MS);
    return () => clearInterval(tickRef.current);
  }, [subscription]);

  // 60s status poll while visible. Skips on the public/auth pages where
  // there is no JWT — fetchSubscriptionStatus would 401 then.
  useEffect(() => {
    if (!subscription) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await fetchSubscriptionStatus();
        if (cancelled || !res?.data) return;
        if (userActions.setSubscription) {
          dispatch(userActions.setSubscription(res.data));
        }
      } catch {
        // 401/402 are expected on transient states; banner stays put.
      }
    };
    const id = setInterval(tick, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [dispatch, subscription]);

  if (!subscription) return null;
  const { status, trial_ends_at: trialEndsAt, current_period_end: periodEnd } = subscription;

  if (status === 'trial' && trialEndsAt) {
    const remainingMs = new Date(trialEndsAt).getTime() - Date.now();
    if (remainingMs <= 0) {
      return null; // about to flip via poll; don't render a stale banner
    }
    const hours = Math.floor(remainingMs / 3_600_000);
    const minutes = Math.floor((remainingMs % 3_600_000) / 60_000);
    if (hours >= 1) {
      return (
        <Banner $tone="info">
          <BannerText>
            {interp(l(1705), [<Strong key="t">{hours}h {minutes}m</Strong>])}
          </BannerText>
          {showUpgradeLink && (
            <UpgradeLink as="span">
              <Link to="/my-account">{l(1708)}</Link>
            </UpgradeLink>
          )}
        </Banner>
      );
    }
    return (
      <Banner $tone="info">
        <BannerText>
          {interp(l(1706), [<Strong key="m">{minutes}m</Strong>, formatHourMinute(trialEndsAt)])}
        </BannerText>
        {showUpgradeLink && (
          <UpgradeLink as="span">
            <Link to="/my-account">{l(1709)}</Link>
          </UpgradeLink>
        )}
      </Banner>
    );
  }

  if (status === 'cancelled' && periodEnd) {
    return (
      <Banner $tone="warn">
        <BannerText>
          {interp(l(1707), [<Strong key="d">{formatDateLong(periodEnd)}</Strong>])}
        </BannerText>
        {showUpgradeLink && (
          <UpgradeLink to="/subscription/upgrade">{l(1710)}</UpgradeLink>
        )}
      </Banner>
    );
  }

  return null;
};

export default TrialBanner;

const Banner = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 14px;
  font-family: 'SatoshiMedium', sans-serif;
  background: ${(props) => (props.$tone === 'warn' ? 'rgba(255, 80, 80, 0.12)' : props.theme.green2)};
  color: ${(props) => (props.$tone === 'warn' ? '#ff7070' : props.theme.green)};
  border: 1px solid ${(props) => (props.$tone === 'warn' ? 'rgba(255, 80, 80, 0.3)' : 'transparent')};
`;

const BannerText = styled.span`
  flex: 1;
  color: inherit;
`;

const Strong = styled.strong`
  font-family: 'SatoshiBold', sans-serif;
  color: inherit;
`;

const UpgradeLink = styled(Link)`
  padding: 4px 12px;
  border-radius: 8px;
  background: ${(props) => props.theme.green};
  color: ${(props) => props.theme.black};
  font-family: 'SatoshiBold', sans-serif;
  font-size: 13px;
  text-decoration: none;
  white-space: nowrap;
  &:hover { filter: brightness(0.95); }
  a { color: inherit; text-decoration: none; }
`;
